import { PropsWithChildren, ReactElement } from "react";

import { withSubComponent } from "@magnetic/higher-order-components";
import { Table } from "@magnetic/table";

import { BulkActionBarProps } from "src/third-party/magnetic/table";

import { TypedAgGridWrapper } from "./grid/typedAgGridWrapper";
import { TypedGrid } from "./grid/typedGrid";
import { TypedMagneticTableWrapper } from "./grid/typedMagneticTableWrapper";
import {
  BaseTypedTableProps,
  Column,
  getTypedTableRendererTypePreference
} from "./grid/typedTableUtil";
import { BaseColumnConfig } from "./tables/columnConfigs/baseColumnConfig";
import { EmptyContainer } from "./emptyContainer";
import { HFlex } from "./hFlex";
import { Results } from "./results";
import { SelectionInfo } from "./selectionInfo";
import { TableData, TypedTableRendererType } from "./typedTableTypes";
import { VFlex } from "./vFlex";

export type { TableData };
export { TypedTableRendererType };

const TypedTableImpl = withSubComponent(
  function <V extends BaseColumnConfig<VT>, VT>(
    props: BaseTypedTableProps<Column<V, VT>>
  ) {
    const {
      displayResultCount,
      displaySelectedCount,
      actions = [],
      tableType = getTypedTableRendererTypePreference(),
      ...rest
    } = props;

    return (
      <VFlex
        id={`${props.name}_result_count`}
        data-qa={`${props.name}_result_count`}
        gap="none"
      >
        {actions && (
          <HFlex
            justify={"flex-end"}
            gap={8}
            className="action-buttons-inline-with-header"
          >
            {actions}
          </HFlex>
        )}
        {displayResultCount && <Results size={props.data?.length ?? 0} />}
        {displaySelectedCount && (
          <SelectionInfo
            selectedCount={Object.keys(props.rowSelection ?? {}).length}
          />
        )}

        <VFlex id={props.name} data-qa={props.name}>
          {!props.data?.length && !props.isLoading ? (
            <EmptyContainer text={`No data found`} />
          ) : tableType === TypedTableRendererType.agGrid ? (
            <TypedAgGridWrapper {...rest}>{props.children}</TypedAgGridWrapper>
          ) : (
            <TypedMagneticTableWrapper {...rest}>
              {props.children}
            </TypedMagneticTableWrapper>
          )}
        </VFlex>
      </VFlex>
    );
  },
  {
    BulkActionBar:
      /**
       * some type safety checks as there are divergent outcomes
       * Optionally, pass in the designated grid type at the page level, or rely on the global setting preference
       */
      ({
        tableType = getTypedTableRendererTypePreference(),
        children
      }: BulkActionBarProps &
        PropsWithChildren<{
          readonly tableType?: TypedTableRendererType;
        }>) => {
        if (
          typeof children !== "string" &&
          typeof children !== "number" &&
          typeof children !== "boolean" &&
          children !== null
        ) {
          return tableType === TypedTableRendererType.agGrid ? (
            <TypedGrid.BulkActionBar>{children}</TypedGrid.BulkActionBar>
          ) : (
            <Table.BulkActionBar>
              {children as ReactElement}
            </Table.BulkActionBar>
          );
        }
        return null;
      }
  }
);

export default TypedTableImpl;
